// src/components/LayoutWithDrawer.js
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import styled from "styled-components";
import { API_ENDPOINTS } from "../config/constants";
import {
  FaHome,
  FaPlus,
  FaListAlt,
  FaUsers,
  FaTags,
  FaUtensils,
  FaCog,
  FaBluetoothB,
  FaConciergeBell,
  FaQrcode,
  FaChair,
  FaRobot,
  FaBoxOpen,
  FaChartLine,
  FaWallet,
  FaFileInvoiceDollar,
  FaBook,
  FaFileInvoice,
  FaCalendarAlt,
  FaChartBar,
  FaReceipt,
  FaChartPie,
  FaUserPlus,
  FaUser,
  FaQuestionCircle,
  FaComments,
  FaBug,
  FaBolt,
  FaKeyboard,
  FaPrint,
  FaUserShield,
  FaHandsHelping,
  FaGift,
  FaUserSecret,
  FaLink,
  FaTrashAlt,
  FaLock,
} from "react-icons/fa";
import {
  getAccountLinkContext,
  hydrateAccountLinkContext,
  SESSION_UPDATED_EVENT,
  switchLinkedRestaurant,
} from "../utils/accountLinks";
import { resolveTaxLabelFromProfile } from "../utils/taxDetails";
import {
  clearImpersonationStorage,
  getImpersonationMeta,
  isImpersonating,
  stopImpersonation,
} from "../utils/impersonation";
import { resetLogRocketSession } from "../utils/logRocket";
import POSLockOverlay from "./POSLockOverlay";
import { hasPOSLockPin, POS_LOCK_EVENT, setPOSLocked } from "../utils/posLock";
import {
  canAccessFeature,
  canAccessRoute,
  getDefaultWebNavigation,
  getProfileRoute,
  normalizeRole,
} from "../utils/rbac";
import {
  dispatchAppEscape,
  dispatchAppShortcut,
  focusAndSelect,
  focusFirstVisible,
  getMatchingShortcut,
  getShortcutSections,
  isEditableTarget,
} from "../utils/shortcuts";
import GlobalQrOrderNotifier from "./GlobalQrOrderNotifier";
import ShortcutHelpOverlay from "./ShortcutHelpOverlay";
import { isMultiplePricingEnabled } from "../utils/priceLists";
import packageJson from "../../package.json";

const BRAND = "#015b9a";
const TRIAL_TOTAL_DAYS = 3;
const TRIAL_ORDER_LIMIT = 25;
const DAY_MS = 1000 * 60 * 60 * 24;

const parseAnyDate = (value) => {
  if (!value) return new Date(0);
  const isoish = typeof value === "string" ? value.replace(" ", "T") : value;
  const dt = new Date(isoish);
  return Number.isNaN(dt.getTime()) ? new Date(0) : dt;
};

const pad2 = (v) => String(v).padStart(2, "0");
const formatDateYMD = (date) => {
  const d = parseAnyDate(date);
  const ts = d?.getTime?.() ?? NaN;
  if (!Number.isFinite(ts) || ts <= 0) {
    const now = new Date();
    return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(
      now.getDate()
    )}`;
  }
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};
const LOGO =
  "https://billsarthi.com/wp-content/uploads/2025/03/logo-white-1.png";
const MOBILE_BREAKPOINT = 900;
const LayoutWithDrawer = ({ children }) => {
  const lockUser = JSON.parse(localStorage.getItem("userData") || "{}")?.data || {};
  const lockRestaurantId = lockUser?.id || lockUser?.restaurant_id;
  const [open, setOpen] = useState(
    typeof window !== "undefined" ? window.innerWidth > MOBILE_BREAKPOINT : true
  );
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= MOBILE_BREAKPOINT : false
  );
  const [menuSearch, setMenuSearch] = useState("");
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showShortcutHelp, setShowShortcutHelp] = useState(false);
  const [trialDaysLeft, setTrialDaysLeft] = useState(null);
  const [trialOrdersCount, setTrialOrdersCount] = useState(null);
  const [trialOrdersLoading, setTrialOrdersLoading] = useState(false);
  const [sessionVersion, setSessionVersion] = useState(0);
  const [posLockConfigured, setPosLockConfigured] = useState(() => hasPOSLockPin(lockRestaurantId));
  const searchInputRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [hoveredTooltip, setHoveredTooltip] = useState({
    visible: false,
    text: "",
    top: 0,
    left: 0,
  });

  const handleNavItemMouseEnter = (text) => (e) => {
    if (!open && !isMobile) {
      const rect = e.currentTarget.getBoundingClientRect();
      setHoveredTooltip({
        visible: true,
        text: text,
        top: rect.top + rect.height / 2,
        left: rect.right + 10,
      });
    }
  };

  const handleNavItemMouseLeave = () => {
    setHoveredTooltip((prev) => ({ ...prev, visible: false }));
  };

  const handleLogout = () => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = () => {
    clearImpersonationStorage();
    resetLogRocketSession();
    localStorage.removeItem("userData");
    setShowLogoutConfirm(false);
    navigate("/login");
  };

  const cancelLogout = () => setShowLogoutConfirm(false);

  useEffect(() => {
    const syncLockConfig = () => setPosLockConfigured(hasPOSLockPin(lockRestaurantId));
    window.addEventListener(POS_LOCK_EVENT, syncLockConfig);
    window.addEventListener("storage", syncLockConfig);
    return () => {
      window.removeEventListener(POS_LOCK_EVENT, syncLockConfig);
      window.removeEventListener("storage", syncLockConfig);
    };
  }, [lockRestaurantId]);

  const handlePOSLock = () => {
    if (!posLockConfigured) {
      window.alert("Set a POS Lock PIN in Printer / POS Settings first.");
      navigate("/printer-settings");
      return;
    }
    setPOSLocked(lockRestaurantId, true);
  };

  const logoutFromPOSLock = () => {
    setPOSLocked(lockRestaurantId, false);
    confirmLogout();
  };

  const handleStopImpersonation = async () => {
    try {
      await stopImpersonation();
      navigate("/dashboard");
    } catch (err) {
      window.alert(err?.message || "Unable to stop impersonation.");
      navigate("/dashboard");
    }
  };

    const storedRestaurantProfile =
      JSON.parse(localStorage.getItem("userData") || "{}")?.data || {};
    const menuTaxLabel = resolveTaxLabelFromProfile(storedRestaurantProfile);
    const baseMenuGroups = [
    {
      title: "Overview",
      items: [
        { label: "Dashboard", route: "/dashboard", icon: FaHome },
        { label: "Notifications", route: "/notifications", icon: FaReceipt },
      ],
    },
    {
      title: "POS & Orders",
      items: [
        {
          label: "Quick Order",
          route: "/order/TakeAway-1",
          icon: FaBolt,
          accent: "green",
          state: { quickBill: true },
        },
        { label: "POS Sales", route: "/tables", icon: FaListAlt },
        { label: "My KOTs", route: "/kot/waiter", icon: FaConciergeBell },
        { label: "Kitchen / KOT", route: "/kot/management", icon: FaConciergeBell },
        { label: "QR Table Orders", route: "/qr/table-order", icon: FaChair },
      ],
    },
    {
      title: "Menu Management",
      items: [
        { label: "Add Category", route: "/add-category", icon: FaTags },
        { label: "Add Product", route: "/add-product", icon: FaUtensils },
        ...(isMultiplePricingEnabled(storedRestaurantProfile)
          ? [{ label: "Price Lists", route: "/price-lists", icon: FaTags }]
          : []),
        { label: "Digital Menu", route: "/digital-menu", icon: FaQrcode },
        { label: "Menu Scanner", route: "/menu-scanner", icon: FaRobot },
        { label: `Bulk ${menuTaxLabel} Update`, route: "/bulk-gst", icon: FaTags },
      ],
    },
    {
      title: "Inventory",
      items: [
        { label: "Stock In", route: "/inventory/stock-in", icon: FaBoxOpen },
        { label: "Usage", route: "/inventory/usage", icon: FaChartLine },
        { label: "Manage Items", route: "/inventory/manage-items", icon: FaBoxOpen },
        { label: "Product Reorder", route: "/product-reorder", icon: FaListAlt },
      ],
    },
    {
      title: "Customers & Credit",
      items: [
        { label: "Party / Customers", route: "/add-party", icon: FaUsers },
        { label: "Khatabook", route: "/khatabook", icon: FaBook },
        { label: "Party Bills", route: "/party-bills", icon: FaFileInvoice },
      ],
    },
    {
      title: "Finance",
      items: [
        { label: "Finance", route: "/finance/tracker", icon: FaWallet },
        { label: "Finance Report", route: "/finance/report", icon: FaFileInvoiceDollar },
        { label: "Day Book Entry Report", route: "/finance/day-book", icon: FaBook },
        { label: "Profit & Loss", route: "/finance/profit-loss", icon: FaChartLine },
        { label: "Monthly Summary", route: "/monthly-summary", icon: FaCalendarAlt },
        { label: "Billing History", route: "/billing/history", icon: FaReceipt },
      ],
    },
    {
      title: "Reports",
      items: [
        { label: "Reports Hub", route: "/reports", icon: FaChartBar },
        { label: "Order Edit History", route: "/order-edit-history", icon: FaReceipt },
        { label: "Deleted Orders", route: "/deleted-orders", icon: FaTrashAlt },
      ],
    },
    {
      title: "Staff",
      items: [
        { label: "Manage Staff", route: "/staff/manage", icon: FaUserPlus },
        { label: "Role Permissions", route: "/role-permissions", icon: FaUserShield },
        { label: "Staff Profile", route: "/waiter/profile", icon: FaUser },
      ],
    },
    {
      title: "Settings",
      items: [
        { label: "Profile", route: "/profile", icon: FaUser },
        { label: "Regional Tax", route: "/regional-tax-settings", icon: FaCog },
        { label: "Add / Manage Tables", route: "/add-table", icon: FaPlus },
        { label: "QR Management", route: "/qr/management", icon: FaQrcode },
        { label: "Printer / POS Settings", route: "/printer-settings", icon: FaPrint },
        { label: "Multiple Printers", route: "/multiple-printers", icon: FaPrint },
        { label: "Connect Bluetooth", route: "/connect-bluetooth", icon: FaBluetoothB },
        { label: "Customize Drawer", route: "/menu-customize", icon: FaListAlt },
        {
          label: "Linked Restaurants",
          route: "/linked-restaurants",
          icon: FaLink,
          ownerOnly: true,
        },
      ],
    },
    {
      title: "Tools",
      items: [
        { label: "Keyboard Shortcuts", route: "/keyboard-shortcuts", icon: FaKeyboard },
        { label: "Calculator", route: "/calculator", icon: FaBolt },
        { label: "Find Us", route: "/find-us", icon: FaLink },
      ],
    },
    {
      title: "Support & Account",
      items: [
        { label: "Help Center", route: "/help", icon: FaQuestionCircle },
        { label: "Login Help", route: "/login-help", icon: FaHandsHelping },
        { label: "Support Chat", route: "/support", icon: FaComments },
        { label: "Feedback Hub", route: "/feedback-hub", icon: FaComments },
        { label: "Feedback & Bugs", route: "/feedback", icon: FaBug },
        { label: "Referral", route: "/referral", icon: FaGift },
        { label: "Policies", route: "/policies", icon: FaBook },
        { label: "PLAN INFO", route: "/plan-selection", icon: FaChartPie },
        {
          label: "Admin Impersonation",
          route: "/admin/impersonation",
          icon: FaUserSecret,
          adminOnly: true,
        },
      ],
    },
  ];
  let menuPreferences = {};
  try { menuPreferences = JSON.parse(localStorage.getItem("web_menu_preferences") || "{}"); } catch (_) {}
  const hiddenMenuRoutes = new Set(menuPreferences.hiddenRoutes || []);
  const menuRouteOrder = Array.isArray(menuPreferences.routeOrder) ? menuPreferences.routeOrder : [];
  const orderIndex = new Map(menuRouteOrder.map((route, index) => [route, index]));
  const menuGroups = baseMenuGroups.map((group) => ({ ...group, items: group.items
    .filter((item) => !hiddenMenuRoutes.has(item.route))
    .sort((a, b) => (orderIndex.get(a.route) ?? 9999) - (orderIndex.get(b.route) ?? 9999)) }));

  // Auto collapse on smaller screens
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
    const onChange = () => {
      setIsMobile(mq.matches);
      setOpen(!mq.matches);
    };
    onChange();
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  useEffect(() => {
    const handleSessionUpdated = () => {
      setSessionVersion((current) => current + 1);
    };

    window.addEventListener(SESSION_UPDATED_EVENT, handleSessionUpdated);
    window.addEventListener("storage", handleSessionUpdated);
    return () => {
      window.removeEventListener(SESSION_UPDATED_EVENT, handleSessionUpdated);
      window.removeEventListener("storage", handleSessionUpdated);
    };
  }, []);

  const user =
    JSON.parse(localStorage.getItem("userData") || "{}")?.data || null;
  const impersonationActive = isImpersonating();
  const impersonationMeta = getImpersonationMeta();
  const canUseImpersonation = canAccessFeature("impersonation", user);
  const linkedAccountContext = getAccountLinkContext();
  const linkedAccounts = linkedAccountContext?.accounts || [];
  const currentRole = normalizeRole(user);
  const defaultNavigation = getDefaultWebNavigation(user);
  const profileRoute = getProfileRoute(user);
  const showLinkedAccountPicker =
    !impersonationActive && linkedAccounts.length > 1;
  const restaurantName = user?.restaurant_name || "Admin Panel";
  const isTrialUser = Number(user?.has_purchased ?? user?.has_purchase ?? 0) === 0;
  const trialOrdersLeft =
    trialOrdersCount === null || trialOrdersCount === undefined
      ? null
      : Math.max(0, TRIAL_ORDER_LIMIT - Number(trialOrdersCount));
  const showTrialStrip =
    open &&
    isTrialUser &&
    trialDaysLeft !== null &&
    trialDaysLeft <= TRIAL_TOTAL_DAYS &&
    trialDaysLeft >= 0;

  useEffect(() => {
    const focusInAppSearch = () => {
      if (
        focusFirstVisible(
          '[data-shortcut-search="page"], [data-shortcut-search="order-products"]'
        )
      ) {
        return true;
      }

      if (focusAndSelect(searchInputRef.current)) return true;

      return focusFirstVisible(
        'input[type="search"], input[placeholder*="Search"], input[placeholder*="search"]'
      );
    };

    const handleShortcut = (shortcut) => {
      switch (shortcut.id) {
        case "global.search":
          if (focusInAppSearch()) return true;
          if (isMobile && !open) {
            setOpen(true);
            setTimeout(() => {
              focusInAppSearch();
            }, 80);
            return true;
          }
          return false;
        case "global.dashboard":
          if (canAccessRoute("/dashboard", user)) {
            navigate("/dashboard");
            return true;
          }
          {
            const fallback = getDefaultWebNavigation(user);
            navigate(fallback.path, fallback.state ? { state: fallback.state } : undefined);
          }
          return true;
        case "global.tables":
          if (canAccessRoute("/tables", user)) {
            navigate("/tables");
            return true;
          }
          {
            const fallback = getDefaultWebNavigation(user);
            navigate(fallback.path, fallback.state ? { state: fallback.state } : undefined);
          }
          return true;
        case "global.quick-order":
          navigate("/order/TakeAway-1", { state: { quickBill: true } });
          return true;
        case "global.reports":
          if (canAccessRoute("/reports", user)) {
            navigate("/reports");
            return true;
          }
          {
            const fallback = getDefaultWebNavigation(user);
            navigate(fallback.path, fallback.state ? { state: fallback.state } : undefined);
          }
          return true;
        case "global.profile":
          navigate(getProfileRoute(user));
          return true;
        case "global.help":
          setShowShortcutHelp(true);
          return true;
        case "global.escape": {
          const handledByPage = !dispatchAppEscape({
            pathname: location.pathname,
          });
          if (handledByPage) return true;
          if (showShortcutHelp) {
            setShowShortcutHelp(false);
            return true;
          }
          if (showLogoutConfirm) {
            setShowLogoutConfirm(false);
            return true;
          }
          if (document.activeElement === searchInputRef.current) {
            searchInputRef.current?.blur?.();
            return true;
          }
          if (isMobile && open) {
            setOpen(false);
            return true;
          }
          return false;
        }
        default:
          return !dispatchAppShortcut(shortcut.id, {
            pathname: location.pathname,
          });
      }
    };

    const onKeyDown = (event) => {
      const shortcut = getMatchingShortcut(event, location.pathname);
      if (!shortcut) return;
      if (event.defaultPrevented && shortcut.id !== "global.escape") return;
      if (showShortcutHelp && shortcut.id !== "global.escape") return;
      if (isEditableTarget(event.target) && !shortcut.allowInInput) return;

      const handled = handleShortcut(shortcut);
      if (handled) event.preventDefault();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    navigate,
    isMobile,
    open,
    showLogoutConfirm,
    showShortcutHelp,
    location.pathname,
    user,
  ]);

  useEffect(() => {
    setShowShortcutHelp(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!user?.id || currentRole !== "owner" || impersonationActive) {
      return undefined;
    }

    hydrateAccountLinkContext().catch(() => {});
    return undefined;
  }, [currentRole, impersonationActive, sessionVersion, user?.id]);

  useEffect(() => {
    let active = true;
    const fetchTrialOrdersCount = async (restaurantId, startDate) => {
      if (!restaurantId) {
        if (active) setTrialOrdersCount(null);
        return;
      }
      try {
        if (active) setTrialOrdersLoading(true);
        const now = new Date();
        const payload = {
          restaurantid: restaurantId,
          fromdate: formatDateYMD(startDate || now),
          todate: formatDateYMD(now),
        };
        const response = await fetch(
          API_ENDPOINTS.REPORT_SALES,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          }
        );
        const result = await response.json().catch(() => ({}));
        if (!active) return;
        if (response.ok) {
          setTrialOrdersCount(Number(result?.total_sale_count ?? 0));
        } else {
          setTrialOrdersCount(null);
        }
      } catch (err) {
        if (active) setTrialOrdersCount(null);
      } finally {
        if (active) setTrialOrdersLoading(false);
      }
    };

    const hydrateTrial = async () => {
      if (!user?.id) {
        if (active) {
          setTrialDaysLeft(null);
          setTrialOrdersCount(null);
        }
        return;
      }
      const hasPurchased = Number(user?.has_purchased ?? 0) !== 0;
      if (hasPurchased) {
        if (active) {
          setTrialDaysLeft(null);
          setTrialOrdersCount(null);
        }
        return;
      }

      const now = new Date();
      const created = parseAnyDate(user?.created_at || user?.createdAt);
      const start = created.getTime() > 0 ? created : now;
      const expiry = parseAnyDate(user?.validupto);
      const validUntil =
        expiry.getTime() > 0
          ? expiry
          : new Date(start.getTime() + TRIAL_TOTAL_DAYS * DAY_MS);
      const rawDaysLeft = Math.max(
        0,
        Math.ceil((validUntil.getTime() - now.getTime()) / DAY_MS)
      );
      const daysLeft =
        rawDaysLeft > TRIAL_TOTAL_DAYS ? null : rawDaysLeft;
      if (active) setTrialDaysLeft(daysLeft);
      await fetchTrialOrdersCount(user?.id, start);
    };

    hydrateTrial();
    return () => {
      active = false;
    };
  }, [
    user?.id,
    user?.has_purchased,
    user?.created_at,
    user?.createdAt,
    user?.validupto,
  ]);

  const handleAccountSwitch = (event) => {
    const nextRestaurantId = Number(event.target.value || 0);
    if (!nextRestaurantId || nextRestaurantId === Number(user?.id || 0)) {
      return;
    }

    try {
      switchLinkedRestaurant(nextRestaurantId);
      navigate("/dashboard");
      if (isMobile) setOpen(false);
    } catch (error) {
      window.alert(error?.message || "Unable to switch restaurant.");
    }
  };

  const filteredGroups = menuSearch.trim()
    ? menuGroups
        .map((group) => ({
          ...group,
          items: group.items.filter((item) => {
            if (item.adminOnly && !canUseImpersonation) return false;
            if (item.ownerOnly && currentRole !== "owner") return false;
            if (!canAccessRoute(item.route, user)) return false;
            const hay = `${item.label} ${item.route} ${group.title}`.toLowerCase();
            return hay.includes(menuSearch.trim().toLowerCase());
          }),
        }))
        .filter((group) => group.items.length > 0)
    : menuGroups
        .map((group) => ({
          ...group,
          items: group.items.filter((item) =>
            item.adminOnly
              ? canUseImpersonation
              : item.ownerOnly
              ? currentRole === "owner"
              : canAccessRoute(item.route, user)
          ),
        }))
        .filter((group) => group.items.length > 0);

  return (
    <Shell $open={open} $mobile={isMobile}>
      {isMobile && open && <Backdrop onClick={() => setOpen(false)} />}
      <Sidebar $open={open} $mobile={isMobile} onMouseLeave={handleNavItemMouseLeave}>
        <SideTop $open={open}>
          {open && (
            <Brand
              onClick={() =>
                navigate(
                  defaultNavigation.path,
                  defaultNavigation.state ? { state: defaultNavigation.state } : undefined
                )
              }
            >
              <LogoDot>
                <FaQrcode size={14} />
              </LogoDot>
              <LogoImg src={LOGO} alt="Bill Sarthi" />
            </Brand>
          )}

          {!isMobile && (
            <Toggle
              aria-label="Toggle sidebar"
              onClick={() => {
                handleNavItemMouseLeave();
                setOpen((s) => !s);
              }}
              onMouseEnter={handleNavItemMouseEnter(!open ? "Expand Sidebar" : "Collapse Sidebar")}
              onMouseLeave={handleNavItemMouseLeave}
            >
              ☰
            </Toggle>
          )}
        </SideTop>

        <Nav onScroll={handleNavItemMouseLeave}>
          {open && (
            <SearchWrap>
              <SearchInput
                ref={searchInputRef}
                data-shortcut-search="sidebar"
                type="text"
                placeholder="Search menu…"
                value={menuSearch}
                onChange={(e) => setMenuSearch(e.target.value)}
              />
            </SearchWrap>
          )}
          {showTrialStrip=="new" && (
            <TrialStrip>
              <TrialHead>
                <TrialTitle>Trial status</TrialTitle>
                {trialOrdersLoading ? <TrialSpinner /> : <TrialArrow>›</TrialArrow>}
              </TrialHead>
              <TrialChips>
                <TrialChip>
                  <strong>{trialDaysLeft ?? "--"}</strong> days left
                </TrialChip>
                <TrialChip>
                  <strong>{trialOrdersLeft ?? "--"}</strong> orders left ({TRIAL_ORDER_LIMIT})
                </TrialChip>
              </TrialChips>
              <TrialFoot>
                After {TRIAL_TOTAL_DAYS} days, access continues until {TRIAL_ORDER_LIMIT} orders are completed.
              </TrialFoot>
            </TrialStrip>
          )}
          {filteredGroups.map((group) => (
            <MenuGroup key={group.title} $open={open}>
              {open && <GroupLabel>{group.title}</GroupLabel>}
              {group.items.map((item) => {
                const active = location.pathname.startsWith(item.route);
                const handleItemClick = () => {
                  if (typeof item.onClick === "function") {
                    item.onClick();
                    if (isMobile) setOpen(false);
                    return;
                  }
                  if (item.state) {
                    navigate(item.route, { state: item.state });
                    if (isMobile) setOpen(false);
                    return;
                  }
                  navigate(item.route);
                  if (isMobile) setOpen(false);
                };
                return (
                <NavItem
                  key={item.route}
                  $active={active}
                  $open={open}
                  $accent={item.accent === "green"}
                  onClick={() => {
                    handleNavItemMouseLeave();
                    handleItemClick();
                  }}
                  onMouseEnter={handleNavItemMouseEnter(item.label)}
                  onMouseLeave={handleNavItemMouseLeave}
                >
                  {open ? (
                    <IconText>
                      <IconBubble aria-label={item.label} $accent={item.accent === "green"}>
                        {item.icon ? <item.icon size={16} color="#fff" /> : "•"}
                      </IconBubble>
                      <span className="label">{item.label}</span>
                    </IconText>
                  ) : (
                    <IconBubble aria-label={item.label} $accent={item.accent === "green"}>
                      {item.icon ? <item.icon size={16} color="#fff" /> : "•"}
                    </IconBubble>
                  )}
                </NavItem>
              );
            })}
          </MenuGroup>
        ))}
      </Nav>

      <SideFoot>
        <NavItem
          as="button"
          $open={open}
          $compact
          onClick={() => {
            handleNavItemMouseLeave();
            navigate("/more");
            if (isMobile) setOpen(false);
          }}
          onMouseEnter={handleNavItemMouseEnter("All Shortcuts")}
          onMouseLeave={handleNavItemMouseLeave}
        >
          {open ? (
            <IconText>
              <IconBubble aria-label="All Shortcuts">⭐</IconBubble>
              <span className="label">All Shortcuts</span>
            </IconText>
          ) : (
            <IconBubble aria-label="All Shortcuts">⭐</IconBubble>
          )}
        </NavItem>
        {open && <VersionLabel>Version {packageJson.version}</VersionLabel>}
        <NavItem
          as="button"
          $danger
          $open={open}
          onClick={() => {
            handleNavItemMouseLeave();
            handleLogout();
            if (isMobile) setOpen(false);
          }}
          onMouseEnter={handleNavItemMouseEnter("Logout")}
          onMouseLeave={handleNavItemMouseLeave}
        >
          {open ? (
            <IconText>
              <IconBubble aria-label="Logout">⏻</IconBubble>
              <span className="label">Logout</span>
            </IconText>
          ) : (
            <IconBubble aria-label="Logout">⏻</IconBubble>
          )}
        </NavItem>
        {open && (
          <FootNote>
            Crafted with <span role="img" aria-label="Heart">❤️</span> in Bharat{" "}
            <span role="img" aria-label="India">🇮🇳</span>
          </FootNote>
        )}
      </SideFoot>
      </Sidebar>

      <Main>
        <TopBar>
          <TopLeft>
            {isMobile && (
              <Toggle
                aria-label="Open menu"
                title="Menu"
                onClick={() => setOpen(true)}
              >
                ☰
              </Toggle>
            )}
            <h1>{restaurantName}</h1>
          </TopLeft>
          <TopRight>
            {showLinkedAccountPicker && (
              <AccountPickerWrap>
                <AccountPickerLabel>Account</AccountPickerLabel>
                <AccountPicker
                  value={String(user?.id || "")}
                  onChange={handleAccountSwitch}
                  aria-label="Switch linked restaurant"
                >
                  {linkedAccounts.map((account) => {
                    const isPrimary =
                      Number(linkedAccountContext?.primary_restaurant_id || 0) ===
                      Number(account?.id || 0);
                    const label =
                      account?.restaurant_name ||
                      account?.name ||
                      `Restaurant ${account?.id}`;
                    return (
                      <option key={account.id} value={String(account.id)}>
                        {isPrimary ? `Main • ${label}` : label}
                      </option>
                    );
                  })}
                </AccountPicker>
              </AccountPickerWrap>
            )}
            {impersonationActive && (
              <TopAction
                type="button"
                onClick={handleStopImpersonation}
                title="Return to admin account"
                style={{ background: "#dc2626", borderColor: "#ef4444", color: "#fff" }}
              >
                Stop Impersonation ({impersonationMeta?.target?.name || "User"})
              </TopAction>
            )}
            <TopAction
              type="button"
              onClick={handlePOSLock}
              title={posLockConfigured ? "Lock POS" : "Set lock PIN in Printer / POS Settings"}
            >
              <FaLock style={{ marginRight: 6 }} /> Lock
            </TopAction>
            <TopAction
              type="button"
              onClick={() => navigate("/tables")}
              title="POS SALE"
              style={{ background: "#fff", color: "#015b9a" }}
            >
              POS SALE
            </TopAction>
            <div
              id="topbar-pos-sale-right"
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            />
            <TopAction
              type="button"
              onClick={() =>
                navigate("/order/TakeAway-1", { state: { quickBill: true } })
              }
              title="QUICK ORDER"
              style={{ background: "#16a34a", color: "#fff" }}
            >
              QUICK ORDER
            </TopAction>
            {location.pathname.startsWith("/dashboard") && (
              <>
                <TopAction
                  type="button"
                  onClick={() =>
                    window.dispatchEvent(new Event("dashboard:sync"))
                  }
                >
                  <span style={{ marginRight: 6 }}>🔄</span>
                  Sync
                </TopAction>
                <TopAction
                  type="button"
                  onClick={() =>
                    window.dispatchEvent(new Event("dashboard:refresh"))
                  }
                >
                  <span style={{ marginRight: 6 }}>↻</span>
                  Refresh
                </TopAction>
              </>
            )}
            <UserChip
              title={user?.name || "User"}
              role="button"
              tabIndex={0}
              onClick={() => navigate(profileRoute)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  navigate(profileRoute);
                }
              }}
            >
              <Avatar>{(user?.name || "U").charAt(0).toUpperCase()}</Avatar>
              <div className="meta">
                <b>{user?.name || "User"}</b>
                <small>{user?.email || "-"}</small>
              </div>
            </UserChip>
          </TopRight>
        </TopBar>

        <Content>{children}</Content>
        <GlobalQrOrderNotifier />
      </Main>

      <ShortcutHelpOverlay
        open={showShortcutHelp}
        onClose={() => setShowShortcutHelp(false)}
        onOpenGuide={() => navigate("/keyboard-shortcuts")}
        sections={getShortcutSections(location.pathname)}
      />

      {showLogoutConfirm && (
        <ConfirmOverlay role="dialog" aria-modal="true" aria-label="Logout confirmation">
          <ConfirmCard>
            <h3>Confirm Logout</h3>
            <p>Are you sure you want to log out from this account?</p>
            <ConfirmActions>
              <ConfirmButton type="button" onClick={cancelLogout}>
                Cancel
              </ConfirmButton>
              <ConfirmButton type="button" $danger onClick={confirmLogout}>
                Yes, Logout
              </ConfirmButton>
            </ConfirmActions>
          </ConfirmCard>
        </ConfirmOverlay>
      )}
      <POSLockOverlay user={user} onLogout={logoutFromPOSLock} />
      {!open && !isMobile && hoveredTooltip.visible && (
        <SidebarTooltip $top={hoveredTooltip.top} $left={hoveredTooltip.left}>
          {hoveredTooltip.text}
        </SidebarTooltip>
      )}
    </Shell>
  );
};

export default LayoutWithDrawer;

/* ===================== styled ===================== */

const Shell = styled.div`
  --side-open: 232px;
  --side-closed: 68px;
  --line: #e5e7eb;
  --muted: #6b7280;
  --bg: #f7f8fa;

  display: grid;
  grid-template-columns: ${({ $open, $mobile }) =>
    $mobile ? "1fr" : $open ? "var(--side-open) 1fr" : "var(--side-closed) 1fr"};
  min-height: 100vh;
  background: var(--bg);
  overflow-x: clip;
`;

const Sidebar = styled.aside`
  position: ${({ $mobile }) => ($mobile ? "fixed" : "sticky")};
  top: 0;
  left: 0;
  height: 100vh;
  background: #083b66; /* a deeper tone under brand */
  color: #fff;
  display: grid;
  grid-template-rows: auto 1fr auto;
  border-right: 1px solid rgba(255, 255, 255, 0.06);
  width: ${({ $open, $mobile }) =>
    $mobile ? "min(84vw, 300px)" : $open ? "var(--side-open)" : "var(--side-closed)"};
  transition: ${({ $mobile }) =>
    $mobile ? "transform 0.25s ease" : "width 0.25s ease"};
  transform: ${({ $open, $mobile }) =>
    $mobile ? ($open ? "translateX(0)" : "translateX(-100%)") : "none"};
  z-index: 120;
  box-shadow: ${({ $mobile, $open }) =>
    $mobile && $open ? "0 0 30px rgba(0,0,0,0.35)" : "none"};
  box-sizing: border-box;
  overflow: hidden;
`;

const SideTop = styled.div`
  display: flex;
  flex-direction: ${({ $open }) => ($open ? "row" : "column")};
  align-items: center;
  justify-content: ${({ $open }) => ($open ? "space-between" : "center")};
  gap: 8px;
  padding: ${({ $open }) => ($open ? "12px 10px 8px" : "10px 4px 8px")};
  width: 100%;
  box-sizing: border-box;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 10px;
  }
`;

const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  justify-content: center;
  cursor: pointer;
  user-select: none;
  span {
    font-weight: 800;
    letter-spacing: 0.2px;
    font-size: 16px;
  }
`;
const LogoDot = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: linear-gradient(135deg, ${BRAND} 0%, #00a0ff 100%);
  box-shadow: 0 4px 16px rgba(0, 160, 255, 0.35);
  display: grid;
  place-items: center;
  color: #fff;
  font-size: 14px;
`;
const LogoImg = styled.img`
  height: 24px;
  object-fit: contain;
`;
const Toggle = styled.button`
  border: none;
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  cursor: pointer;
  font-size: 16px;
  display: grid;
  place-items: center;
  transition: background 0.15s ease;
  &:hover {
    background: rgba(255, 255, 255, 0.16);
  }
`;

const Nav = styled.nav`
  padding: 6px 6px 10px;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-content: start;
  width: 100%;
  box-sizing: border-box;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.3) transparent;
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.25);
    border-radius: 999px;
  }
`;
const SearchWrap = styled.div`
  padding: 4px 10px 2px;
`;
const SearchInput = styled.input`
  width: 100%;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  border-radius: 8px;
  padding: 6px 8px;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.2;
  outline: none;
  &::placeholder {
    color: rgba(255, 255, 255, 0.7);
  }
`;
const TrialStrip = styled.div`
  margin: 6px 8px 2px;
  padding: 10px;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(250, 204, 21, 0.2), rgba(245, 158, 11, 0.2));
  border: 1px solid rgba(250, 204, 21, 0.35);
  color: #fde68a;
  display: grid;
  gap: 6px;
`;
const TrialHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;
const TrialTitle = styled.span`
  font-weight: 800;
  font-size: 12px;
  color: #fbbf24;
  text-transform: uppercase;
  letter-spacing: 0.6px;
`;
const TrialArrow = styled.span`
  width: 18px;
  height: 18px;
  border-radius: 999px;
  background: rgba(251, 191, 36, 0.25);
  color: #fef3c7;
  display: grid;
  place-items: center;
  font-weight: 900;
  font-size: 12px;
`;
const TrialChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;
const TrialChip = styled.div`
  border-radius: 999px;
  padding: 4px 8px;
  background: rgba(251, 191, 36, 0.18);
  color: #fde68a;
  font-weight: 700;
  font-size: 11px;
  strong {
    color: #fef3c7;
  }
`;
const TrialFoot = styled.div`
  font-size: 10.5px;
  color: rgba(253, 230, 138, 0.9);
  font-weight: 600;
  line-height: 1.3;
`;
const TrialSpinner = styled.span`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid rgba(253, 230, 138, 0.25);
  border-top-color: #fde68a;
  animation: spin 0.9s linear infinite;
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
const NavItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${({ $open }) => ($open ? "flex-start" : "center")};
  gap: ${({ $open }) => ($open ? "10px" : "0")};
  padding: ${({ $compact, $open }) =>
    $compact ? ($open ? "8px 10px" : "8px 0") : $open ? "10px 12px" : "8px 0"};
  margin: 4px 0;
  border-radius: 12px;
  cursor: pointer;
  user-select: none;
  border: 1px solid transparent;
  width: 100%;
  box-sizing: border-box;
  background: ${({ $active, $accent }) =>
    $accent
      ? "linear-gradient(135deg, rgba(22,163,74,0.9), rgba(16,185,129,0.9))"
      : $active
      ? "rgba(255,255,255,0.12)"
      : "transparent"};
  border-color: ${({ $accent }) =>
    $accent ? "rgba(255,255,255,0.25)" : "transparent"};
  color: #fff;
  box-shadow: ${({ $accent }) =>
    $accent ? "0 10px 24px rgba(16,185,129,0.25)" : "none"};
  transition: background 0.15s ease, transform 0.05s ease, box-shadow 0.15s ease;

  &:hover {
    background: ${({ $active, $accent }) =>
      $accent
        ? "linear-gradient(135deg, rgba(16,185,129,0.95), rgba(22,163,74,0.95))"
        : $active
        ? "rgba(255,255,255,0.16)"
        : "rgba(255,255,255,0.08)"};
  }
  & .label {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
    font-size: ${({ $compact }) => ($compact ? "12px" : "14px")};
  }

  ${({ $danger }) =>
    $danger &&
    `
    background: rgba(239,68,68,.2);
    border-color: rgba(239,68,68,.6);
    box-shadow: 0 8px 20px rgba(239,68,68,.18);
    &:hover{ background: rgba(239,68,68,.28); }
  `}
`;

const IconBubble = styled.span`
  width: 28px;
  height: 28px;
  border-radius: 10px;
  background: ${({ $accent }) => ($accent ? "rgba(15,118,54,0.6)" : "rgba(255, 255, 255, 0.12)")};
  color: #fff;
  display: grid;
  place-items: center;
  font-size: 16px;
`;
const IconText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const GroupLabel = styled.div`
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 1.1px;
  color: #bde8ff;
  padding: 9px 10px 5px;
  font-weight: 900;
`;

const MenuGroup = styled.div`
  display: grid;
  gap: 2px;
  padding: ${({ $open }) => ($open ? "2px 3px 7px" : "0")};
  border-bottom: 1px solid rgba(255, 255, 255, 0.09);
  &:last-child { border-bottom: 0; }
`;

const SideFoot = styled.div`
  padding: 8px 6px 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 100%;
  box-sizing: border-box;
`;
const FootNote = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  text-align: center;
  padding: 6px 8px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.08);
`;

const VersionLabel = styled.div`
  width: 100%;
  box-sizing: border-box;
  padding: 2px 8px;
  color: rgba(255, 255, 255, 0.68);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-align: center;
`;

const Main = styled.div`
  display: grid;
  grid-template-rows: auto 1fr;
  min-width: 0;
  min-height: 100vh;
`;

const TopBar = styled.header`
  position: sticky;
  top: 0;
  z-index: 10;
  background: ${BRAND};
  color: #fff;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 14px;
  flex-wrap: nowrap;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 10px;
    gap: 8px;
    flex-wrap: wrap;
  }
`;
const TopLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;

  h1 {
    font-size: clamp(16px, 2.5vw, 20px);
    font-weight: 800;
    letter-spacing: 0.2px;
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: min(60vw, 440px);
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: 100%;
    h1 {
      max-width: calc(100vw - 92px);
    }
  }
`;
const TopRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: 100%;
    overflow-x: auto;
    padding-bottom: 2px;
    scrollbar-width: thin;
    -webkit-overflow-scrolling: touch;
  }
`;
const AccountPickerWrap = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.18);
  min-width: 0;
`;
const AccountPickerLabel = styled.span`
  font-size: 11px;
  font-weight: 800;
  color: rgba(255, 255, 255, 0.82);
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;
const AccountPicker = styled.select`
  border: 0;
  outline: 0;
  background: transparent;
  color: #fff;
  font-weight: 700;
  font-size: 12px;
  min-width: 148px;
  cursor: pointer;

  option {
    color: #0f172a;
  }
`;
const TopAction = styled.button`
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  border-radius: 999px;
  padding: 6px 12px;
  font-weight: 800;
  font-size: 12px;
  white-space: nowrap;
  cursor: pointer;
  transition: transform 0.12s ease, background 0.12s ease;
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    padding: 6px 10px;
    font-size: 11px;
  }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 110;
`;

const UserChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.18);
  cursor: pointer;

  .meta {
    display: grid;
    line-height: 1.05;
  }
  b {
    font-size: 12px;
  }
  small {
    font-size: 11px;
    opacity: 0.9;
  }

  @media (max-width: 640px) {
    padding: 6px 8px;
    .meta {
      display: none;
    }
  }
`;
const Avatar = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: #ffffff;
  color: ${BRAND};
  font-weight: 900;
  display: grid;
  place-items: center;
`;

const Content = styled.main`
  padding: clamp(10px, 2vw, 16px);
  overflow: auto;
  background: #f7f8fa;
  border-left: 1px solid #eef2f7;
  min-width: 0;

  > * {
    min-width: 0;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    border-left: none;
  }
`;

const ConfirmOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 200;
  background: rgba(2, 6, 23, 0.58);
  backdrop-filter: blur(4px);
  display: grid;
  place-items: center;
  padding: 16px;
`;

const ConfirmCard = styled.div`
  width: min(420px, 92vw);
  border-radius: 16px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid #dbeafe;
  box-shadow: 0 22px 50px rgba(2, 6, 23, 0.25);
  padding: 20px;
  h3 {
    margin: 0 0 6px;
    color: #0f172a;
    font-size: 20px;
  }
  p {
    margin: 0;
    color: #475569;
    font-size: 14px;
  }
`;

const ConfirmActions = styled.div`
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const ConfirmButton = styled.button`
  border-radius: 10px;
  border: 1px solid ${({ $danger }) => ($danger ? "rgba(239,68,68,.7)" : "#cbd5e1")};
  background: ${({ $danger }) =>
    $danger ? "linear-gradient(135deg,#ef4444,#dc2626)" : "#fff"};
  color: ${({ $danger }) => ($danger ? "#fff" : "#0f172a")};
  font-weight: 700;
  font-size: 13px;
  padding: 9px 14px;
  cursor: pointer;
  transition: transform 0.12s ease, filter 0.12s ease;
  &:hover {
    transform: translateY(-1px);
    filter: brightness(1.02);
  }
`;

const SidebarTooltip = styled.div`
  position: fixed;
  top: ${({ $top }) => `${$top}px`};
  left: ${({ $left }) => `${$left}px`};
  transform: translateY(-50%);
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  color: #ffffff;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 12.5px;
  font-weight: 600;
  letter-spacing: 0.2px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 99999;
  box-shadow: 0 12px 28px -4px rgba(0, 0, 0, 0.45), 0 6px 12px -4px rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.2);
  animation: sidebarTooltipFadeIn 0.15s cubic-bezier(0.16, 1, 0.3, 1);

  &::before {
    content: "";
    position: absolute;
    right: 100%;
    top: 50%;
    transform: translateY(-50%);
    border-width: 5px;
    border-style: solid;
    border-color: transparent #0f172a transparent transparent;
  }

  @keyframes sidebarTooltipFadeIn {
    from {
      opacity: 0;
      transform: translateY(-50%) translateX(-6px);
    }
    to {
      opacity: 1;
      transform: translateY(-50%) translateX(0);
    }
  }
`;
